import { useEffect, useState } from 'react'
import { Calculator, Play, TrendingUp, Award, Wallet, Layers, RefreshCw } from 'lucide-react'
import api from '../api/client'
import Loader from '../components/Loader'
import { errorModal } from '../components/ui'

// Simulador do Motor de Remuneração — espelha o motor REAL (POST /motor-remuneracao/simular),
// sem gravar nada. Escolhe serviço + parâmetros e mostra status/comissões/PV/wallet estimada.
const money = (v) => 'R$ ' + Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })
const inp = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-accent-400'

export default function SimuladorMotor() {
  const [servicos, setServicos] = useState([])
  const [f, setF] = useState({ servico_slug: '', valor: '', pv: '', equipe: '', clientes: '', recorrencia: '' })
  const [res, setRes] = useState(null)
  const [busy, setBusy] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api.get('/motor-remuneracao/servicos').then((r) => {
      const lst = (r.data?.data || r.data || []).filter((s) => s.ativo !== false)
      setServicos(lst)
      if (lst[0]) setF((x) => ({ ...x, servico_slug: lst[0].slug }))
    }).catch(() => setServicos([])).finally(() => setLoading(false))
  }, [])

  async function simular() {
    if (!f.servico_slug) return errorModal('Selecione o serviço.')
    if (!Number(f.valor)) return errorModal('Informe o valor da venda.')
    setBusy(true)
    try {
      const body = {
        servico_slug: f.servico_slug, valor: Number(f.valor),
        pv: f.pv !== '' ? Number(f.pv) : undefined,
        equipe: Number(f.equipe || 0), clientes: Number(f.clientes || 0),
        recorrencia: Number(f.recorrencia || 0),
      }
      const { data } = await api.post('/motor-remuneracao/simular', body)
      setRes(data)
    } catch (e) { errorModal(e?.response?.data?.detail || 'Erro ao simular') } finally { setBusy(false) }
  }

  if (loading) return <div className="flex justify-center py-20"><Loader text="Carregando serviços..." /></div>

  const Item = ({ icon: Icon, label, value, color }) => (
    <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
      <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center shadow mb-2`}><Icon size={16} className="text-white" /></div>
      <p className="text-lg font-heading font-bold text-graphite">{value}</p>
      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
    </div>
  )

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <h1 className="text-xl sm:text-2xl font-heading font-extrabold text-graphite flex items-center gap-2">
          <Calculator size={24} className="text-accent-500" /> Simulador do Motor
        </h1>
        <p className="text-sm text-gray-500 mt-1">Calcula o retorno de uma venda usando exatamente a lógica do motor de produção — <b>sem gravar</b> nada na rede.</p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Serviço *</label>
          <select className={inp} value={f.servico_slug} onChange={(e) => setF({ ...f, servico_slug: e.target.value })}>
            {servicos.length === 0 && <option value="">Nenhum serviço configurado</option>}
            {servicos.map((s) => <option key={s.slug} value={s.slug}>{s.nome}</option>)}
          </select>
        </div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">Valor da venda (R$) *</label>
          <input className={inp} type="number" value={f.valor} onChange={(e) => setF({ ...f, valor: e.target.value })} placeholder="399,00" /></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">PV pessoal (opcional)</label>
          <input className={inp} type="number" value={f.pv} onChange={(e) => setF({ ...f, pv: e.target.value })} placeholder="deriva do valor" /></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">Equipe (nº na rede)</label>
          <input className={inp} type="number" value={f.equipe} onChange={(e) => setF({ ...f, equipe: e.target.value })} placeholder="0" /></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">Clientes diretos</label>
          <input className={inp} type="number" value={f.clientes} onChange={(e) => setF({ ...f, clientes: e.target.value })} placeholder="0" /></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">Recorrência mensal (R$)</label>
          <input className={inp} type="number" value={f.recorrencia} onChange={(e) => setF({ ...f, recorrencia: e.target.value })} placeholder="0,00" /></div>
        <div className="sm:col-span-2 lg:col-span-3">
          <button onClick={simular} disabled={busy}
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white px-5 py-2.5 text-sm font-bold shadow-md disabled:opacity-50">
            {busy ? <RefreshCw size={16} className="animate-spin" /> : <Play size={16} />} {busy ? 'Simulando...' : 'Simular'}
          </button>
        </div>
      </div>

      {res && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="px-3 py-1 rounded-full text-sm font-bold text-white" style={{ background: res.status_cor || '#16a34a' }}>{res.status}</span>
            <span className="text-sm text-gray-500">{res.servico} · {res.usa_regra_faixa ? 'regra por faixa' : 'regra do serviço'}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Item icon={TrendingUp} label="Comissão direta" value={money(res.comissao_direta)} color="from-emerald-400 to-teal-500" />
            <Item icon={Layers} label="Comissão indireta" value={money(res.comissao_indireta)} color="from-indigo-400 to-violet-500" />
            <Item icon={RefreshCw} label="Recorrência" value={money(res.recorrencia)} color="from-sky-400 to-blue-500" />
            <Item icon={Award} label="Cashback" value={money(res.cashback)} color="from-amber-400 to-orange-500" />
            <Item icon={TrendingUp} label="Pontuação (PV)" value={res.pontuacao ?? 0} color="from-fuchsia-400 to-purple-500" />
            <Item icon={Wallet} label="Wallet estimada" value={money(res.wallet_estimada)} color="from-primary-500 to-accent-500" />
          </div>
          {res.premiacao && <p className="text-sm bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 text-amber-800"><Award size={14} className="inline mr-1" /> Premiação da faixa: <b>{res.premiacao}</b></p>}
          <p className="text-xs text-gray-400">Simulação — nenhum valor foi gravado. Espelha o motor de produção.</p>
        </div>
      )}
    </div>
  )
}
